import React, { useState, useEffect, useMemo } from "react";
import InputPassword from '../ferramentas/inputPassword/inputPassword';
import { useDataManagement } from "../ferramentas/RequisitarAPI.ts";
import style from './ModalStyles.module.css';
import ToastMessage from "../ferramentas/toastMessage/ToastMessage";
import passwordCheck from '../ferramentas/passwordCheck/passwordCheck';
import Placeholder from "../ferramentas/placeholder/Placeholder.js";

const UsuarioModal = ({ onClose, onSave, onEditProfile, initialData }) => {
  const [formData, setFormData] = useState({
    nome: "", email: "", telefone: "", pais: "", novasenha: "", confirmarSenha: "", senha: ""
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [passwordToConfirm, setPasswordToConfirm] = useState("");
  const [isPasswordSectionVisible, setIsPasswordSectionVisible] = useState(false);

  const [users, setUsers] = useState({});
  const { users: fetchUsers } = useDataManagement();

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditing = !!initialData;

  const originalState = useMemo(() => {
    if (!isEditing) return null;
    return {
      nome: initialData.nome || "",
      email: initialData.email || "",
      telefone: initialData.telefone || ""
    };
  }, [initialData, isEditing]);

  useEffect(() => {
    if (isEditing) {
      setFormData({
        nome: initialData.nome || "",
        email: initialData.email || "",
        telefone: initialData.telefone || "",
        pais: initialData.pais || "",
        novasenha: "",
        confirmarSenha: "",
        senha: ""
      });
      setIsPasswordSectionVisible(false);
    } else {
      setFormData({
        nome: "",
        email: "",
        telefone: "",
        pais: "",
        novasenha: "",
        confirmarSenha: "",
        senha: ""
      });
    }
  }, [initialData, isEditing]);

  useEffect(() => {
    const loadUsers = async () => {
      const result = await fetchUsers.list();
      if (result.ok) setUsers(result.data);
    };
    loadUsers();
  }, [fetchUsers]);

  const hasChanges = useMemo(() => {
    if (!isEditing) return true;
    return (
      originalState.nome !== formData.nome ||
      originalState.email !== formData.email ||
      originalState.telefone !== formData.telefone ||
      formData.novasenha !== ""
    );
  }, [formData, originalState, isEditing]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === "telefone") {
      setIsPhoneValid(e.target?.phoneContext?.isValid || false);
      setFormData(prev => ({ ...prev, [name]: value, country: e.target.phoneContext?.country }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear the error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handlers específicos para campos de senha
  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, novasenha: value }));
    if (errors.novasenha) {
      setErrors(prev => ({ ...prev, novasenha: null }));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, confirmarSenha: value }));
    if (errors.confirmarSenha) {
      setErrors(prev => ({ ...prev, confirmarSenha: null }));
    }
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    const { nome, email, telefone, novasenha, confirmarSenha } = formData;
    let newErrors = {};

    if (!nome.trim()) newErrors.nome = "Por favor, preencha Nome.";
    if (!email.trim()) newErrors.email = "Por favor, preencha Email.";
    if (!telefone.trim()) newErrors.telefone = "Por favor, preencha Telefone.";
    if (telefone.trim() && !isPhoneValid) newErrors.telefone = "Telefone inválido.";

    if (novasenha) {
      if (novasenha !== confirmarSenha) {
        newErrors.confirmarSenha = "As novas senhas não coincidem!";
      }
      const pwdError = passwordCheck(novasenha, false);
      if (pwdError) {
        newErrors.novasenha = pwdError;
      }
    } else if (!isEditing) {
      newErrors.novasenha = "A senha é obrigatória para novos usuários.";
    }

    if (email.trim()) {
      const emailExists = Object.values(users).some(
        (user) => user.email.toLowerCase() === email.trim().toLowerCase()
      );
      if (emailExists && (!isEditing || initialData.email.toLowerCase() !== email.trim().toLowerCase())) {
        newErrors.email = "Este e-mail já está cadastrado.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    if (isEditing && hasChanges) {
      setShowConfirmModal(true);
    } else if (!isEditing) {
      handleFinalSubmit();
    }
  };

  const handleApiErrors = (errorMsg) => {
    let lowerMsg = "";
    if (typeof errorMsg === 'string') {
      lowerMsg = errorMsg.toLowerCase();
    } else if (errorMsg && typeof errorMsg === 'object' && errorMsg.erro) {
      lowerMsg = errorMsg.erro.toLowerCase();
    } else if (errorMsg && typeof errorMsg === 'object' && errorMsg.message) {
      lowerMsg = (errorMsg.message.erro || errorMsg.message).toLowerCase();
    } else {
      lowerMsg = String(errorMsg).toLowerCase();
    }

    const unmappedMsg = typeof errorMsg === 'object' ? (errorMsg.erro || errorMsg.message?.erro || JSON.stringify(errorMsg)) : errorMsg;

    setShowConfirmModal(false);

    if (lowerMsg.includes('e-mail') || lowerMsg.includes('email')) {
      setErrors(prev => ({ ...prev, email: unmappedMsg }));
    } else if (lowerMsg.includes('senha') || lowerMsg.includes('password')) {
      if (isEditing && !formData.novasenha) {
        ToastMessage({ cod: "e", texto: unmappedMsg });
      } else {
        setErrors(prev => ({ ...prev, novasenha: unmappedMsg }));
      }
    } else if (lowerMsg.includes('telefone') || lowerMsg.includes('celular') || lowerMsg.includes('phone')) {
      setErrors(prev => ({ ...prev, telefone: unmappedMsg }));
    } else if (lowerMsg.includes('nome') || lowerMsg.includes('nome completo')) {
      setErrors(prev => ({ ...prev, nome: unmappedMsg }));
    } else {
      ToastMessage({ cod: "e", texto: unmappedMsg });
    }
  };

  const handleFinalSubmit = async () => {

    if (isEditing && !passwordToConfirm) {
      ToastMessage({ cod: "e", texto: "Por favor, digite sua senha para confirmar." });
      return;
    }

    let userData;

    // CASO 1: EDITAR PERFIL
    if (isEditing) {
      userData = {
        fullname: formData.nome.trim(),
        email: formData.email.trim(),
        phone: formData.telefone.trim(),
        country: formData.country,
        password: passwordToConfirm, // Senha atual para verificação
        newpassword: formData.novasenha,
        confirmpassword: formData.confirmarSenha
      };
      const res = await onEditProfile(userData);
      if (res && res.error) {
        return handleApiErrors(res.error);
      }
    }
    // CASO 2: CADASTRAR USUÁRIO
    else {
      userData = {
        fullname: formData.nome.trim(),
        email: formData.email.trim(),
        phone: formData.telefone.trim(),
        country: formData.country,
        password: formData.novasenha // Senha inicial do novo usuário
      };

      const res = await onSave(userData);
      if (res && res.error) {
        return handleApiErrors(res.error);
      }
    }

    // Reset dos estados
    setShowConfirmModal(false);
    // setPasswordToConfirm("");
    // onClose();
  };

  const renderConfirmationModal = () => (
    <div className={style.confirmationModalOverlay}>
      <div className={style.confirmationModalContent} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.confirmationModalTitle}>Confirmar Alterações</h3>
        <p className={style.confirmationModalDescription}>
          Para sua segurança, por favor, digite sua senha atual para salvar.
        </p>
        <InputPassword
          label="Sua Senha Atual"
          name="passwordToConfirm"
          autoFocus
          value={passwordToConfirm}
          onChange={(e) => setPasswordToConfirm(e.target.value)}
        />
        <div className={style.confirmationModalFooter}>
          <button
            className={`${style.modalButton} ${style.cancelButton}`}
            onClick={() => {
              setShowConfirmModal(false);
              setPasswordToConfirm("");
            }}
          >
            Cancelar
          </button>
          <button
            className={`${style.modalButton} ${style.saveButton}`}
            onClick={handleFinalSubmit}
          >
            Confirmar e Salvar
          </button>
        </div>
      </div>
    </div>
  );

  const renderPasswordChecklist = (password) => {
    if (!password && !isEditing) return null;
    if (isEditing && !password) return null;

    const reqs = [
      { id: 'length', label: '8 a 16 caracteres', valid: password.length >= 8 && password.length <= 16 },
      { id: 'lower', label: 'Letra minúscula', valid: /[a-z]/.test(password) },
      { id: 'upper', label: 'Letra maiúscula', valid: /[A-Z]/.test(password) },
      { id: 'number', label: 'Número', valid: /[0-9]/.test(password) },
      { id: 'special', label: 'Caractere especial', valid: /[!@#$%^&*(),.?":{}|<>~^/[\];=+-]/.test(password) }
    ];

    const allValid = reqs.every(r => r.valid);
    if (allValid) return null;

    return (
      <ul className={style.passwordChecklist}>
        {reqs.map((r) => (
          <li key={r.id} className={r.valid ? style.validCheck : style.invalidCheck}>
            {r.valid ? '✓' : '•'} {r.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={style.modal} onClick={onClose}>
      <div className={style.modal_content} onClick={(e) => e.stopPropagation()}>
        {showConfirmModal && renderConfirmationModal()}

        <div className={style.modalHeader}>
          <h2>{isEditing ? "Editar Perfil" : "Cadastrar Usuário"}</h2>
          <span className={style.close} onClick={onClose}>×</span>
        </div>

        <div className={style.modalBody}>
          <form id="usuarioForm" className={style.modalForm} onSubmit={handlePreSubmit}>
            <div className={style.formSection}>
              <h4 className={style.formSectionTitle}>Dados Pessoais</h4>
              <div className={style.modalForm}>
                <div className={style.fullWidth}>
                  <Placeholder
                    label="Nome Completo"
                    type="text"
                    name="nome"
                    required
                    value={formData.nome}
                    onChange={handleInputChange}
                    error={!!errors.nome}
                  />
                  {errors.nome && <div className={style.errorText}>{errors.nome}</div>}
                </div>
                <div>
                  <Placeholder
                    label="E-mail"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    error={!!errors.email}
                  />
                  {errors.email && <div className={style.errorText}>{errors.email}</div>}
                </div>
                <div>
                  <Placeholder
                    label="Celular"
                    type="tel"
                    name="telefone"
                    country={formData.country}
                    required
                    value={formData.telefone}
                    onChange={handleInputChange}
                    error={!!errors.telefone}
                  />
                  {errors.telefone && <div className={style.errorText}>{errors.telefone}</div>}
                </div>
              </div>
            </div>

            <div className={style.formSection}>

              {!isEditing && (
                <>
                  <h4 className={style.formSectionTitle}>Senha</h4>
                  <div className={style.inputPassword}>
                    <InputPassword
                      label="Senha"
                      name="novasenha"
                      required
                      value={formData.novasenha}
                      onChange={handlePasswordChange}
                      msgErro={errors.novasenha}
                    />
                    {renderPasswordChecklist(formData.novasenha)}
                  </div>
                  <div className={style.inputPassword}>
                    <InputPassword
                      label="Confirme a Senha"
                      name="confirmarSenha"
                      required
                      value={formData.confirmarSenha}
                      onChange={handleConfirmPasswordChange}
                      msgErro={errors.confirmarSenha}
                    />
                  </div>
                </>
              )}

              {isEditing && (
                <>
                  <button
                    type="button"
                    className={style.toggleSectionLink}
                    onClick={() => setIsPasswordSectionVisible(!isPasswordSectionVisible)}
                    style={{ cursor: 'pointer' }}
                  >
                    {isPasswordSectionVisible ? 'Cancelar Alteração de Senha' : 'Alterar Senha'}
                  </button>

                  {isPasswordSectionVisible && (
                    <div className={`${style.passwordSection} ${style.visible}`}>
                      <div>
                        <InputPassword
                          label="Nova Senha"
                          name="novasenha"
                          required={isPasswordSectionVisible}
                          value={formData.novasenha}
                          onChange={handlePasswordChange}
                          msgErro={errors.novasenha}
                        />
                        {renderPasswordChecklist(formData.novasenha)}
                      </div>
                      <InputPassword
                        label="Confirme a Nova Senha"
                        name="confirmarSenha"
                        required={!!formData.novasenha}
                        value={formData.confirmarSenha}
                        onChange={handleConfirmPasswordChange}
                        disabled={!formData.novasenha}
                        msgErro={errors.confirmarSenha}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </form>
        </div>

        <div className={style.modalFooter}>
          <button
            className={`${style.modalButton} ${style.cancelButton}`}
            type="button"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className={`${style.modalButton} ${style.saveButton}`}
            type="submit"
            form="usuarioForm"
            disabled={isEditing && !hasChanges}
          >
            {isEditing ? "Salvar Alterações" : "Cadastrar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsuarioModal;